import { Fragment, useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { NewsCard } from "@/components/NewsCard";
import { Flame, Heart, Video, RefreshCw, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import LoadMoreFooter from "@/components/LoadMoreFooter";
import { useSearchParams, useLocation } from "react-router-dom";
import LatestNewsWidget from "@/components/LatestNewsWidget";
import MostRead from "@/components/MostRead";
import { considerBreaking } from "@/lib/breaking";
import { useLang } from "@/lib/i18n";
import { useInfiniteArticles } from "@/hooks/useInfiniteArticles";
import { resetHomeMeta, setPageMeta } from "@/lib/seoMeta";

function SectionHeader({ icon: Icon, title, accent }) {
    return (
        <div className="flex items-center gap-3 mb-6 border-b border-gray-200 pb-3">
            <div className={`h-8 w-1.5 rounded-sm ${accent || "bg-brand-red"}`} />
            <h2 className="font-display font-black text-2xl md:text-3xl text-brand-black flex items-center gap-2">
                {Icon && <Icon className="w-6 h-6 text-brand-red" />} {title}
            </h2>
        </div>
    );
}

export default function HomePage() {
    const { t, lang } = useLang();
    const [home, setHome] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async (silent = false) => {
        if (!silent) { setLoading(true); setError(null); }
        try {
            let payload = null;
            if (typeof window !== "undefined" && window.__homePreload__) {
                const pre = await window.__homePreload__;
                window.__homePreload__ = null;
                if (window.__homePreloadLang__ === lang) payload = pre;
            }
            if (!payload) {
                const r = await api.get("/public/home", { params: silent ? { _ts: Date.now() } : undefined });
                payload = r.data;
            }
            setHome(payload);
            window.dispatchEvent(new CustomEvent("home:loaded"));
            considerBreaking([payload?.featured, ...(payload?.latest || []), ...(payload?.breaking || [])]);
        } catch (e) {
            if (!silent) setError(t("loadFail", e?.response?.status));
        } finally {
            if (!silent) setLoading(false);
        }
    }, [lang, t]);

    // Infinite «أحدث الأخبار» — cursor-paginated; live events mutate it in place.
    const [sp] = useSearchParams();
    const { pathname, search } = useLocation();
    const page = Math.max(1, parseInt(sp.get("page") || "1", 10) || 1);
    const feed = useInfiniteArticles({ pageSize: 10, initialSkip: (page - 1) * 10, auto: false });
    const { prepend, replace, remove } = feed;
    useEffect(() => { if (home) window.prerenderReady = true; }, [home]);

    useEffect(() => {
        document.title = `${t("siteName")} | ${t("homeTitle")}`;
        resetHomeMeta(lang);
        if (page > 1) setPageMeta(pathname, search);
        load();
        const onApply = async (e) => {
            if (Array.isArray(e.detail)) prepend(e.detail);
            await load(true);
            window.dispatchEvent(new CustomEvent("articles:applied", { detail: e.detail }));
        };
        const onUpdate = (e) => { replace(e.detail || []); load(true); };
        const onRemoved = (e) => { remove(e.detail || []); load(true); };
        window.addEventListener("articles:apply", onApply);
        window.addEventListener("articles:update", onUpdate);
        window.addEventListener("articles:removed", onRemoved);
        return () => {
            window.removeEventListener("articles:apply", onApply);
            window.removeEventListener("articles:update", onUpdate);
            window.removeEventListener("articles:removed", onRemoved);
        };
    }, [load, prepend, replace, remove, t, lang, page, pathname, search]);

    if (loading && !home) return <div className="p-10 text-center text-gray-500" data-testid="home-loading">{t("loading")}</div>;
    if (error && !home) return (
        <div className="mx-auto max-w-md p-10 text-center" data-testid="home-error">
            <AlertTriangle className="w-10 h-10 text-brand-red mx-auto mb-3" />
            <p className="text-gray-700 mb-4 text-sm">{error}</p>
            <Button onClick={load} className="bg-brand-red hover:bg-brand-red-dark rounded-sm" data-testid="home-retry"><RefreshCw className="w-4 h-4" /> {t("retry")}</Button>
        </div>
    );
    if (!home) return null;

    // Dedupe: on mobile the featured article is the FIRST card in the latest
    // grid (per product spec), so the standalone hero must not render there.
    // On desktop the standalone hero stays. We compute the mobile grid by
    // prepending featured to the paginated list (skipping if already there).
    const featuredId = home.featured?.id;
    const mobileGridItems = (() => {
        const seen = new Set();
        const out = [];
        if (home.featured) { out.push(home.featured); seen.add(home.featured.id); }
        for (const a of feed.items) {
            if (seen.has(a.id)) continue;
            seen.add(a.id);
            out.push(a);
        }
        return out;
    })();
    const desktopGridItems = (() => {
        const seen = new Set(featuredId ? [featuredId] : []);
        return feed.items.filter((a) => {
            if (seen.has(a.id)) return false;
            seen.add(a.id);
            return true;
        });
    })();

    return (
        <div className="mx-auto max-w-7xl px-4 py-8">
            {/* ----- MOBILE ORDER (< lg): ticker (handled by PublicLayout) →
                     «آخر الأخبار» → «أحدث الأخبار» with featured as first card.
                    Rendered only < lg. ---------------------------------------- */}
            <div className="lg:hidden space-y-6 mb-8" data-testid="home-mobile">
                <LatestNewsWidget mobile />
                <div>
                    <SectionHeader icon={Flame} title={t("latest")} />
                    <div className="news-grid grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 items-start" data-testid="latest-grid-mobile">
                        {mobileGridItems.map((a, i) => (
                            <Fragment key={a.id}>
                                <NewsCard article={a} variant={i === 0 && a.id === featuredId ? "hero" : "default"} />
                                {i === Math.min(11, mobileGridItems.length - 1) && <div className="sm:col-span-2"><MostRead testId="most-read-mobile" /></div>}
                            </Fragment>
                        ))}
                    </div>
                    <LoadMoreFooter feed={feed} testId="home-load-more" />
                </div>
            </div>

            {/* ----- DESKTOP ORDER (≥ lg): unchanged — hero + latest column
                     on the right (main), «آخر الأخبار» on the left (sticky). --- */}
            <section className="hidden lg:grid grid-cols-12 gap-4 md:gap-5 items-start mb-14" data-testid="home-desktop">
                <div className="col-span-8 space-y-6">
                    {home.featured && <NewsCard article={home.featured} variant="hero" />}
                    <div>
                        <SectionHeader icon={Flame} title={t("latest")} />
                        <div className="news-grid grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 items-start" data-testid="latest-grid">
                            {desktopGridItems.map((a) => <NewsCard key={a.id} article={a} />)}
                        </div>
                        <LoadMoreFooter feed={feed} testId="home-load-more-desktop" />
                    </div>
                </div>
                <HomeAside />
            </section>

            {home.human.length > 0 && (
                <section className="mb-14 -mx-4 px-4 py-10 bg-gray-50">
                    <div className="mx-auto max-w-7xl">
                        <SectionHeader icon={Heart} title={t("humanStories")} />
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                            {home.human.map((a) => <NewsCard key={a.id} article={a} />)}
                        </div>
                    </div>
                </section>
            )}

            {home.videos.length > 0 && (
                <section className="mb-14">
                    <SectionHeader icon={Video} title={t("videos")} />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                        {home.videos.map((a) => <NewsCard key={a.id} article={a} />)}
                    </div>
                </section>
            )}
        </div>
    );
}

/** Sidebar: sticky (below the real header height) only while it fits the viewport; otherwise it scrolls with the page
 *  so every «الأكثر قراءة» row is reachable without loading the archive. No inner scroll container. */
function HomeAside() {
    const ref = useRef(null);
    const [sticky, setSticky] = useState(false);
    const [top, setTop] = useState(16);
    useEffect(() => {
        const el = ref.current; if (!el) return;
        const measure = () => {
            const h = document.querySelector(".site-header")?.offsetHeight || 0;
            setTop(h + 16);
            setSticky(el.offsetHeight + h + 32 <= window.innerHeight);
        };
        measure();
        const ro = new ResizeObserver(measure); ro.observe(el);
        const hdr = document.querySelector(".site-header"); if (hdr) ro.observe(hdr);
        window.addEventListener("resize", measure);
        return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
    }, []);
    return (
        <aside ref={ref} className={`col-span-4 space-y-6 self-start ${sticky ? "sticky" : ""}`} style={sticky ? { top } : undefined} data-testid="home-aside" data-sticky={sticky ? "1" : "0"}>
            <LatestNewsWidget />
            <MostRead testId="most-read-desktop" />
        </aside>
    );
}

